import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/company";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Completa email y contraseña");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenida");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#050F26] to-[#0B2D5C] flex items-center justify-center p-6"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center mb-8">
          <div className="bg-white p-3 rounded-sm">
            <img src={LOGO_URL} alt="Great Solutions US" className="h-12 w-auto" />
          </div>
          <div className="mt-3 text-white font-display font-bold text-lg tracking-wide">
            GREAT SOLUTIONS US
          </div>
        </Link>

        <div className="bg-white rounded-sm border border-white/10 shadow-2xl p-8">
          <div className="text-xs font-semibold text-[#0B2D5C] tracking-[0.2em] uppercase mb-2">
            Panel administrativo
          </div>
          <h1 className="font-display text-2xl font-bold text-[#0f172a] mb-2">Iniciar sesión</h1>
          <p className="text-sm text-[#475569] mb-6">
            Acceso solo para personal autorizado de Great Solutions US.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <Label htmlFor="email" className="text-[#0f172a] font-medium">
                Email
              </Label>
              <div className="relative mt-2">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]"
                />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@greatsolutionsus.com"
                  className="pl-10 h-12 border-[#e2e8f0] rounded-sm focus-visible:ring-[#0B2D5C]"
                  data-testid="admin-login-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[#0f172a] font-medium">
                Contraseña
              </Label>
              <div className="relative mt-2">
                <Lock
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]"
                />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 h-12 border-[#e2e8f0] rounded-sm focus-visible:ring-[#0B2D5C]"
                  data-testid="admin-login-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0B2D5C] hover:bg-[#081F42] text-white rounded-sm text-base"
              data-testid="admin-login-submit"
            >
              <LogIn size={16} className="mr-2" />
              {loading ? "Ingresando..." : "Ingresar"}
            </Button>
          </form>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-white/60 hover:text-white transition-colors">
            ← Volver al sitio
          </Link>
        </div>
      </div>
    </div>
  );
}
